"use client";

import _defineProperty from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _createForOfIteratorHelper from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper";
import _slicedToArray from "/home/jenkins/workspace/tServices_Forms_Client_CICD_main/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useCollectionContext } from "./VDHZ5F7K.js";
import { createElement, forwardRef } from "./LMDWO4NN.js";
import { useStoreState } from "./YV4JVR4I.js";
import { useBooleanEvent, useEvent, useForceUpdate, useId, useMergeRefs, useWrapElement } from "./ABQUS43J.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js"; // src/collection/collection-renderer.tsx

import { getScrollingElement, getWindow } from "@ariakit/core/utils/dom";
import { invariant, shallowEqual } from "@ariakit/core/utils/misc";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { jsx } from "react/jsx-runtime";
var TagName = "div";
var CollectionRendererContext = createContext(null);

function createTask() {
  var raf = 0;

  var run = function run(cb) {
    if (raf) return;
    raf = requestAnimationFrame(function () {
      raf = 0;
      cb();
    });
  };

  var cancel = function cancel() {
    cancelAnimationFrame(raf);
    raf = 0;
  };

  return {
    run: run,
    cancel: cancel
  };
}

function findNearestIndex(items, target, getValue) {
  var left = 0;
  var right = getItemsLength(items) - 1;

  while (left <= right) {
    var index = (left + right) / 2 | 0;
    var value = getValue(index);
    if (value === target) return index;else if (value < target) left = index + 1;else right = index - 1;
  }

  if (left > 0) return left - 1;
  return 0;
}

function getItemsLength(items) {
  return typeof items === "number" ? items : items.length;
}

function getItemObject(item) {
  if (!item || typeof item !== "object") {
    return {
      value: item
    };
  }

  return item;
}

function getItemId(item, index, baseId) {
  var _a;

  invariant(baseId, "CollectionRenderer must be given an `id` prop.");
  var defaultId = "".concat(baseId, "/").concat(index);
  return (_a = getItemObject(item).id) != null ? _a : defaultId;
}

function getItem(items, index) {
  if (typeof items === "number") {
    if (index >= items) return null;
    return {};
  }

  var item = items[index];
  if (!item) return null;
  if (typeof item === "object") return item;
  return {
    value: item
  };
}

function getItemSize(item, horizontal, fallbackElement) {
  var _a, _b, _c, _d, _e;

  var itemObject = getItemObject(item);
  horizontal = itemObject.orientation === "horizontal" || horizontal;
  var prop = horizontal ? "width" : "height";
  var style = itemObject.style;

  if (style) {
    var size = style[prop];
    if (typeof size === "number") return size;
  }

  var items = itemObject.items;

  if (items == null ? void 0 : items.length) {
    var hasSameOrientation = !itemObject.orientation || horizontal && itemObject.orientation === "horizontal" || !horizontal && itemObject.orientation === "vertical";
    var paddingStart = (_b = (_a = itemObject.paddingStart) != null ? _a : itemObject.padding) != null ? _b : 0;
    var paddingEnd = (_d = (_c = itemObject.paddingEnd) != null ? _c : itemObject.padding) != null ? _d : 0;
    var padding = hasSameOrientation ? paddingStart + paddingEnd : 0;
    var initialSize = ((_e = itemObject.gap) != null ? _e : 0) * (items.length - 1) + padding;

    if (hasSameOrientation && itemObject.itemSize) {
      return initialSize + itemObject.itemSize * items.length;
    }

    var totalSize = items.reduce(function (sum, item2) {
      return sum + getItemSize(item2, horizontal);
    }, initialSize);
    if (totalSize !== initialSize) return totalSize;
  }

  var element = fallbackElement !== false ? itemObject.element || fallbackElement : null;

  if (element == null ? void 0 : element.isConnected) {
    return element.getBoundingClientRect()[prop];
  }

  return 0;
}

function getAverageSize(props) {
  var length = getItemsLength(props.items);
  var currentIndex = 0;
  var averageSize = props.estimatedItemSize;

  var setAverageSize = function setAverageSize(size) {
    var prevIndex = currentIndex;
    currentIndex = currentIndex + 1;
    averageSize = (averageSize * prevIndex + size) / currentIndex;
  };

  for (var index = 0; index < length; index += 1) {
    var item = getItem(props.items, index);
    var itemId = getItemId(item, index, props.baseId);
    var itemData = props.data.get(itemId);
    var fallbackElement = props.elements.get(itemId);
    var size = getItemSize(item, props.horizontal, fallbackElement);

    if (size) {
      setAverageSize(size);
    } else if (itemData == null ? void 0 : itemData.rendered) {
      setAverageSize(itemData.end - itemData.start);
    }
  }

  return averageSize;
}

function getScrollOffset(scroller, horizontal) {
  if ("scrollX" in scroller) {
    return horizontal ? scroller.scrollX : scroller.scrollY;
  }

  return horizontal ? scroller.scrollLeft : scroller.scrollTop;
}

function getViewport(scroller) {
  var _scroller$ownerDocume = scroller.ownerDocument,
      defaultView = _scroller$ownerDocume.defaultView,
      documentElement = _scroller$ownerDocume.documentElement;
  if (scroller === documentElement) return defaultView;
  return scroller;
}

function useScroller(rendererRef) {
  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      scroller = _useState2[0],
      setScroller = _useState2[1];

  useEffect(function () {
    var renderer = rendererRef == null ? void 0 : rendererRef.current;
    if (!renderer) return;
    var scroller2 = getScrollingElement(renderer);
    if (!scroller2) return;
    setScroller(scroller2);
  }, [rendererRef]);
  return scroller;
}

function getRendererOffset(renderer, scroller, horizontal) {
  var win = getWindow(renderer);
  var htmlElement = win == null ? void 0 : win.document.documentElement;
  var rendererRect = renderer.getBoundingClientRect();
  var rendererOffset = horizontal ? rendererRect.left : rendererRect.top;

  if (scroller === htmlElement) {
    var scrollOffset2 = getScrollOffset(win, horizontal);
    return scrollOffset2 + rendererOffset;
  }

  var scrollerRect = scroller.getBoundingClientRect();
  var scrollerOffset = horizontal ? scrollerRect.left : scrollerRect.top;
  var scrollOffset = getScrollOffset(scroller, horizontal);
  return rendererOffset - scrollerOffset + scrollOffset;
}

function getOffsets(renderer, scroller, horizontal) {
  var scrollOffset = getScrollOffset(scroller, horizontal);
  var rendererOffset = getRendererOffset(renderer, scroller, horizontal);
  var scrollSize = horizontal ? scroller.clientWidth : scroller.clientHeight;
  var start = scrollOffset - rendererOffset;
  var end = start + scrollSize;
  return {
    start: start,
    end: end
  };
}

function getItemsEnd(props) {
  var length = getItemsLength(props.items);
  var totalPadding = props.paddingStart + props.paddingEnd;
  if (!length) return totalPadding;
  var lastIndex = length - 1;
  var totalGap = lastIndex * props.gap;

  if (props.itemSize != null) {
    return length * props.itemSize + totalGap + totalPadding;
  }

  var defaultEnd = length * props.estimatedItemSize + totalGap + totalPadding;
  if (!props.baseId) return defaultEnd;
  var lastItem = getItem(props.items, lastIndex);
  var lastItemId = getItemId(lastItem, lastIndex, props.baseId);
  var lastItemData = props.data.get(lastItemId);
  if (lastItemData == null ? void 0 : lastItemData.end) return lastItemData.end + props.paddingEnd;
  if (!Array.isArray(props.items)) return defaultEnd;
  var end = props.items.reduce(function (sum, item) {
    return sum + getItemSize(item, props.horizontal, false);
  }, 0);
  if (!end) return defaultEnd;
  return end + totalGap + totalPadding;
}

function getData(props) {
  var _a;

  var length = getItemsLength(props.items);
  var nextData;
  var start = props.paddingStart;
  var avgSize = getAverageSize(props);

  var _loop = function _loop(index) {
    var item = getItem(props.items, index);
    var itemId = getItemId(item, index, props.baseId);
    var itemData = props.data.get(itemId);
    var prevRendered = (_a = itemData == null ? void 0 : itemData.rendered) != null ? _a : false;

    var setSize = function setSize(size2) {
      var rendered = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : prevRendered;
      start = start ? start + props.gap : start;
      var end = start + size2;
      var nextItemData = {
        index: index,
        rendered: rendered,
        start: start,
        end: end
      };

      if (!shallowEqual(itemData, nextItemData)) {
        if (!nextData) {
          nextData = new Map(props.data);
        }

        nextData.set(itemId, {
          index: index,
          rendered: rendered,
          start: start,
          end: end
        });
      }

      start = end;
    };

    var size = getItemSize(item, props.horizontal, props.elements.get(itemId));

    if (size) {
      setSize(size, true);
    } else if (itemData == null ? void 0 : itemData.rendered) {
      setSize(itemData.end - itemData.start, true);
    } else {
      setSize(avgSize);
    }
  };

  for (var index = 0; index < length; index += 1) {
    _loop(index);
  }

  return nextData;
}

function useCollectionRenderer(_a) {
  var _b = _a,
      store = _b.store,
      itemsProp = _b.items,
      _b$initialItems = _b.initialItems,
      initialItems = _b$initialItems === void 0 ? 0 : _b$initialItems,
      _b$gap = _b.gap,
      gap = _b$gap === void 0 ? 0 : _b$gap,
      itemSize = _b.itemSize,
      _b$estimatedItemSize = _b.estimatedItemSize,
      estimatedItemSize = _b$estimatedItemSize === void 0 ? 40 : _b$estimatedItemSize,
      overscanProp = _b.overscan,
      orientationProp = _b.orientation,
      _b$padding = _b.padding,
      padding = _b$padding === void 0 ? 0 : _b$padding,
      _b$paddingStart = _b.paddingStart,
      paddingStart = _b$paddingStart === void 0 ? padding : _b$paddingStart,
      _b$paddingEnd = _b.paddingEnd,
      paddingEnd = _b$paddingEnd === void 0 ? padding : _b$paddingEnd,
      persistentIndices = _b.persistentIndices,
      _b$renderOnScroll = _b.renderOnScroll,
      renderOnScroll = _b$renderOnScroll === void 0 ? true : _b$renderOnScroll,
      _b$renderOnResize = _b.renderOnResize,
      renderOnResize = _b$renderOnResize === void 0 ? !!renderOnScroll : _b$renderOnResize,
      renderItem = _b.children,
      props = __objRest(_b, ["store", "items", "initialItems", "gap", "itemSize", "estimatedItemSize", "overscan", "orientation", "padding", "paddingStart", "paddingEnd", "persistentIndices", "renderOnScroll", "renderOnResize", "children"]);

  var _a2, _b2;

  var context = useCollectionContext();
  store = store || context;
  var items = useStoreState(store, function (state) {
    return itemsProp != null ? itemsProp : state == null ? void 0 : state.items;
  });
  invariant(items != null, process.env.NODE_ENV !== "production" && "CollectionRenderer must be either wrapped in a Collection component or be given an `items` prop.");
  var parent = useContext(CollectionRendererContext);

  if (store && (parent == null ? void 0 : parent.store) !== store) {
    parent = null;
  }

  var parentData = parent == null ? void 0 : parent.childrenData;
  var orientation = (_a2 = orientationProp != null ? orientationProp : parent == null ? void 0 : parent.orientation) != null ? _a2 : "vertical";
  var overscan = (_b2 = overscanProp != null ? overscanProp : parent == null ? void 0 : parent.overscan) != null ? _b2 : 1;
  var ref = useRef(null);
  var baseId = useId(props.id);
  var horizontal = orientation === "horizontal";
  var elements = useMemo(function () {
    return /* @__PURE__ */new Map();
  }, []);

  var _useForceUpdate = useForceUpdate(),
      _useForceUpdate2 = _slicedToArray(_useForceUpdate, 2),
      elementsUpdated = _useForceUpdate2[0],
      updateElements = _useForceUpdate2[1];

  var _useState3 = useState(function () {
    if (!initialItems) return [];
    var length = getItemsLength(items);
    var initialLength = Math.min(length, Math.abs(initialItems));
    return Array.from({
      length: initialLength
    }, function (_, index) {
      if (initialItems < 0) return length - index - 1;
      return index;
    });
  }),
      _useState4 = _slicedToArray(_useState3, 2),
      defaultVisibleIndices = _useState4[0],
      setVisibleIndices = _useState4[1];

  var visibleIndices = useMemo(function () {
    if (!persistentIndices) return defaultVisibleIndices;
    var nextIndices = defaultVisibleIndices.slice();

    var _iterator = _createForOfIteratorHelper(persistentIndices),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var index = _step.value;
        if (index < 0) continue;
        if (nextIndices.includes(index)) continue;
        nextIndices.push(index);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    nextIndices.sort(function (a, b) {
      return a - b;
    });

    if (shallowEqual(defaultVisibleIndices, nextIndices)) {
      return defaultVisibleIndices;
    }

    return nextIndices;
  }, [defaultVisibleIndices, persistentIndices]);

  var _useState5 = useState(function () {
    if (!baseId) return /* @__PURE__ */new Map();
    var data2 = (parentData == null ? void 0 : parentData.get(baseId)) || /* @__PURE__ */new Map();
    if (itemSize != null) return data2;
    if (!items) return data2;
    var nextData = getData({
      baseId: baseId,
      items: items,
      data: data2,
      gap: gap,
      elements: elements,
      horizontal: horizontal,
      paddingStart: paddingStart,
      itemSize: itemSize,
      estimatedItemSize: estimatedItemSize
    });
    return nextData || data2;
  }),
      _useState6 = _slicedToArray(_useState5, 2),
      data = _useState6[0],
      setData = _useState6[1];

  var totalSize = useMemo(function () {
    return getItemsEnd({
      baseId: baseId,
      items: items,
      data: data,
      gap: gap,
      horizontal: horizontal,
      itemSize: itemSize,
      estimatedItemSize: estimatedItemSize,
      paddingStart: paddingStart,
      paddingEnd: paddingEnd
    });
  }, [baseId, items, data, gap, horizontal, itemSize, estimatedItemSize, paddingStart, paddingEnd]);
  useEffect(function () {
    if (!baseId) return;
    parentData == null ? void 0 : parentData.set(baseId, data);
  }, [baseId, parentData, data]);
  useEffect(function () {
    if (itemSize != null) return;
    if (!baseId) return;
    if (!items) return;
    var nextData = getData({
      baseId: baseId,
      items: items,
      data: data,
      gap: gap,
      elements: elements,
      horizontal: horizontal,
      paddingStart: paddingStart,
      itemSize: itemSize,
      estimatedItemSize: estimatedItemSize
    });

    if (nextData) {
      setData(nextData);
    }
  }, [elementsUpdated, itemSize, baseId, items, data, gap, elements, horizontal, paddingStart, estimatedItemSize]);
  var scroller = useScroller(items ? ref : null);
  var offsetsRef = useRef({
    start: 0,
    end: 0
  });
  var processVisibleIndices = useCallback(function () {
    var offsets = offsetsRef.current;
    if (!items) return;
    if (!baseId) return;
    if (!offsets.end) return;
    if (!data.size && !itemSize) return;
    var length = getItemsLength(items);

    var getItemOffset = function getItemOffset(index) {
      var prop = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "start";

      var _a3;

      if (itemSize) {
        var start2 = itemSize * index + gap * index + paddingStart;
        if (prop === "start") return start2;
        return start2 + itemSize;
      }

      var item = getItem(items, index);
      var itemId = getItemId(item, index, baseId);
      var itemData = data.get(itemId);
      return (_a3 = itemData == null ? void 0 : itemData[prop]) != null ? _a3 : 0;
    };

    var initialStart = findNearestIndex(items, offsets.start, getItemOffset);
    var initialEnd = initialStart;

    while (initialEnd < length && getItemOffset(initialEnd) < offsets.end) {
      initialEnd += 1;
    }

    var finalOverscan = initialEnd - initialStart ? overscan : 0;
    var start = Math.max(initialStart - finalOverscan, 0);
    var end = Math.min(initialEnd + finalOverscan, length);
    var indices = Array.from({
      length: end - start
    }, function (_, index) {
      return index + start;
    });
    setVisibleIndices(function (prevIndices) {
      if (shallowEqual(prevIndices, indices)) return prevIndices;
      return indices;
    });
  }, [elementsUpdated, items, baseId, data, itemSize, gap, paddingStart, overscan]);
  useEffect(processVisibleIndices, [processVisibleIndices]);
  var processVisibleIndicesEvent = useEvent(processVisibleIndices);
  useEffect(function () {
    var renderer = ref.current;
    if (!renderer) return;
    if (!scroller) return;
    offsetsRef.current = getOffsets(renderer, scroller, horizontal);
    processVisibleIndicesEvent();
  }, [scroller, horizontal, processVisibleIndicesEvent]);
  var mayRenderOnScroll = !!renderOnScroll;
  var renderOnScrollProp = useBooleanEvent(renderOnScroll);
  useEffect(function () {
    if (!mayRenderOnScroll) return;
    var renderer = ref.current;
    if (!renderer) return;
    if (!scroller) return;
    var viewport = getViewport(scroller);
    if (!viewport) return;
    var task = createTask();

    var onScroll = function onScroll(event) {
      task.run(function () {
        if (!renderOnScrollProp(event)) return;
        offsetsRef.current = getOffsets(renderer, scroller, horizontal);
        processVisibleIndicesEvent();
      });
    };

    viewport.addEventListener("scroll", onScroll, {
      passive: true
    });
    return function () {
      task.cancel();
      viewport.removeEventListener("scroll", onScroll);
    };
  }, [mayRenderOnScroll, scroller, renderOnScrollProp, horizontal, processVisibleIndicesEvent]);
  var mayRenderOnResize = !!renderOnResize;
  var renderOnResizeProp = useBooleanEvent(renderOnResize);
  useEffect(function () {
    if (!mayRenderOnResize) return;
    var renderer = ref.current;
    if (!renderer) return;
    if (!scroller) return;
    var viewport = getViewport(scroller);
    if (!viewport) return;
    var task = createTask();

    if (viewport === scroller) {
      if (typeof ResizeObserver !== "function") return;
      var firstRun = true;
      var observer = new ResizeObserver(function () {
        if (firstRun) {
          firstRun = false;
          return;
        }

        task.run(function () {
          if (!renderOnResizeProp(scroller)) return;
          offsetsRef.current = getOffsets(renderer, scroller, horizontal);
          processVisibleIndicesEvent();
        });
      });
      observer.observe(scroller);
      return function () {
        task.cancel();
        observer.disconnect();
      };
    }

    var onResize = function onResize() {
      task.run(function () {
        if (!renderOnResizeProp(scroller)) return;
        offsetsRef.current = getOffsets(renderer, scroller, horizontal);
        processVisibleIndicesEvent();
      });
    };

    viewport.addEventListener("resize", onResize, {
      passive: true
    });
    return function () {
      task.cancel();
      viewport.removeEventListener("resize", onResize);
    };
  }, [mayRenderOnResize, scroller, renderOnResizeProp, horizontal, processVisibleIndicesEvent]);
  useEffect(function () {
    if (typeof IntersectionObserver !== "function") return;
    var renderer = ref.current;
    if (!renderer) return;
    if (!scroller) return;
    var viewport = getViewport(scroller);
    if (!viewport) return;
    var observer = new IntersectionObserver(function () {
      offsetsRef.current = getOffsets(renderer, scroller, horizontal);
      processVisibleIndicesEvent();
    }, {
      root: scroller === viewport ? scroller : null
    });
    observer.observe(renderer);
    return function () {
      observer.disconnect();
    };
  }, [scroller, processVisibleIndicesEvent]);
  var elementObserver = useMemo(function () {
    if (typeof ResizeObserver !== "function") return;
    return new ResizeObserver(function () {
      flushSync(updateElements);
    });
  }, [updateElements]);
  var itemRef = useCallback(function (element) {
    if (!element) return;
    if (itemSize) return;
    updateElements();
    elements.set(element.id, element);
    elementObserver == null ? void 0 : elementObserver.observe(element);
  }, [itemSize, elements, updateElements, elementObserver]);
  var getItemProps = useCallback(function (item, index) {
    var _a3, _b3;

    var itemId = getItemId(item, index, baseId);
    var offset = itemSize ? paddingStart + itemSize * index + gap * index : (_b3 = (_a3 = data.get(itemId)) == null ? void 0 : _a3.start) != null ? _b3 : 0;
    var baseItemProps = {
      id: itemId,
      ref: itemRef,
      index: index,
      style: {
        position: "absolute",
        left: horizontal ? offset : 0,
        top: horizontal ? 0 : offset
      }
    };

    if (itemSize) {
      baseItemProps.style[horizontal ? "width" : "height"] = itemSize;
    }

    if (item == null) return baseItemProps;
    var itemProps = getItemObject(item);
    return __spreadProps(__spreadValues(__spreadValues({}, itemProps), baseItemProps), {
      style: __spreadValues(__spreadValues({}, itemProps.style), baseItemProps.style)
    });
  }, [baseId, data, itemSize, paddingStart, gap, horizontal, itemRef]);
  var itemsProps = useMemo(function () {
    return visibleIndices.map(function (index) {
      if (index < 0) return;
      var item = getItem(items, index);
      if (!item) return;
      return getItemProps(item, index);
    }).filter(function (value) {
      return value != null;
    });
  }, [items, visibleIndices, getItemProps]);
  var children = itemsProps == null ? void 0 : itemsProps.map(function (itemProps) {
    return renderItem == null ? void 0 : renderItem(itemProps);
  });
  var styleProp = props.style;
  var sizeProperty = horizontal ? "width" : "height";
  var style = useMemo(function () {
    return __spreadValues(_defineProperty({
      flex: "none",
      position: "relative"
    }, sizeProperty, totalSize), styleProp);
  }, [styleProp, sizeProperty, totalSize]);
  var childrenData = useMemo(function () {
    return /* @__PURE__ */new Map();
  }, []);
  var providerValue = useMemo(function () {
    return {
      store: store,
      orientation: orientation,
      overscan: overscan,
      childrenData: childrenData
    };
  }, [store, orientation, overscan, childrenData]);
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(CollectionRendererContext.Provider, {
      value: providerValue,
      children: element
    });
  }, [providerValue]);
  props = __spreadProps(__spreadValues({
    id: baseId
  }, props), {
    style: style,
    ref: useMergeRefs(ref, props.ref)
  });
  return __spreadProps(__spreadValues({}, props), {
    children: children
  });
}

var CollectionRenderer = forwardRef(function CollectionRenderer2(props) {
  var htmlProps = useCollectionRenderer(props);
  return createElement(TagName, htmlProps);
});
var getCollectionRendererItem = getItem;
var getCollectionRendererItemId = getItemId;
export { useCollectionRenderer, CollectionRenderer, getCollectionRendererItem, getCollectionRendererItemId };